import { useCallback, useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  List,
  X,
  Trash2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Star,
  Calendar,
  Award,
  PenTool,
  CheckCircle2,
  Pencil,
  Square,
  Circle,
  Pentagon,
  ChevronDown,
  Bookmark,
  BookmarkPlus,
  Loader2,
  CalendarDays,
  BarChart3,
  RefreshCw,
} from "lucide-react";
import { DayPicker } from "react-day-picker";
import { it } from "date-fns/locale";
import {
  typologyLabels,
  typologyColorMap,
  getTypologyConfig,
  getTypologyLabel,
} from "../lib/categoryConfig";

// Define DateRange type (not exported in react-day-picker v9+)
type DateRange = { from: Date | undefined; to?: Date | undefined };
import "react-day-picker/style.css";
import type { DrawingShapeType } from "../stores/searchStore";
import { MapView } from "../components/map/MapView";
import { ResultsList } from "../components/search/ResultsList";
import { ZoneAutocomplete } from "../components/search/ZoneAutocomplete";
import { Button } from "../components/ui/Button";
import { CategoryTypeSelect } from "../components/ui/CategoryTypeSelect";
import { useFilterOptions, type CategoryTypeGroup } from "../hooks/useOperators";
import { useSupercluster } from "../hooks/useSupercluster";
import { useSavedSearches } from "../hooks/useSavedSearches";
import { useSearchStore } from "../stores/searchStore";
import type { Zone } from "../hooks/useZoneSearch";
import { useRequireAuth } from "../hooks/useAuth";
import { Spinner } from "../components/ui/Spinner";
import { cn } from "../lib/utils";
import { AnalyticsCard } from "../components/analytics/AnalyticsCard";
import type { OperatorWithCoords, SavedSearch } from "../types/database";
import { getCategoryConfig, getCategoryLabel } from "../lib/categoryConfig";
import type { Polygon as GeoPolygon } from "geojson";

// Legend categories are now dynamically generated from database categories
// See useMemo below that creates legendCategories from database data

const normalizeArray = (value?: string[]) => (value || []).slice().sort();
const areArraysEqual = (a?: string[], b?: string[]) => {
  const left = normalizeArray(a);
  const right = normalizeArray(b);
  if (left.length !== right.length) return false;
  return left.every((item, idx) => item === right[idx]);
};

// ═══════════════════════════════════════════════════════════════
// STATS PREVIEW CHIP - Mini preview of analytics with donut chart
// ═══════════════════════════════════════════════════════════════
interface StatsPreviewChipProps {
  operators: OperatorWithCoords[];
  filteredOperators: OperatorWithCoords[];
  operatorsInView: OperatorWithCoords[];
  isLoading: boolean;
  isOpen: boolean;
  onToggle: () => void;
  selectedCategories?: string[];
  categoryLabelMap?: Record<string, string>;
}

// Mini donut chart component
function MiniDonut({
  segments,
  size = 32,
}: {
  segments: { percentage: number; color: string }[];
  size?: number;
}) {
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  let currentOffset = 0;

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="hsl(var(--secondary))"
        strokeWidth={strokeWidth}
      />
      {segments.map((seg, idx) => {
        const segmentLength = (seg.percentage / 100) * circumference;
        const offset = currentOffset;
        currentOffset += segmentLength;
        if (seg.percentage < 1) return null;
        return (
          <circle
            key={idx}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={seg.color}
            strokeWidth={strokeWidth}
            strokeDasharray={`${segmentLength} ${circumference}`}
            strokeDashoffset={-offset}
            strokeLinecap="butt"
            className="transition-all duration-500"
          />
        );
      })}
    </svg>
  );
}

function StatsPreviewChip({
  operators,
  filteredOperators,
  operatorsInView,
  isLoading,
  isOpen,
  onToggle,
  selectedCategories = [],
  categoryLabelMap = {},
}: StatsPreviewChipProps) {
  // Use same logic as AnalyticsCard: filteredOperators when filters active, otherwise allOperators
  const hasFilters = filteredOperators.length !== operators.length;
  const statsSource = hasFilters ? filteredOperators : operators;

  // Compute category stats for donut (from visible operators)
  const categoryStats = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const op of statsSource) {
      const cat = op.macro_category || "Altro";
      counts[cat] = (counts[cat] || 0) + 1;
    }
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([label, value]) => ({
        label: getCategoryLabel(label),
        rawLabel: label,
        value,
        color: getCategoryConfig(label).color,
        percentage:
          statsSource.length > 0 ? (value / statsSource.length) * 100 : 0,
      }));
  }, [statsSource]);


  const total = operators.length;
  const filteredTotal = filteredOperators.length;
  const inView = operatorsInView.length;
  const disabled = isLoading || total === 0;

  // Determine which count to display: prioritize inView, then filteredTotal if filters active, otherwise total
  // This ensures the displayed number matches the donut chart statistics (statsSource)
  const displayCount = inView > 0 ? inView : (hasFilters ? filteredTotal : total);

  // Determine label: if single category selected, show category name; otherwise show "visibili", "filtrati", or "operatori"
  const getDisplayLabel = (): string => {
    // If categories are selected, check if they all map to the same display category
    if (selectedCategories.length > 0) {
      // Get unique display names for all selected categories
      const displayNames = new Set<string>();
      for (const category of selectedCategories) {
        const displayName = categoryLabelMap[category] || getCategoryLabel(category);
        displayNames.add(displayName);
      }

      // If all selected categories map to the same display name, show that name
      if (displayNames.size === 1) {
        const displayName = Array.from(displayNames)[0];
        return displayName.toLowerCase();
      }
    }

    // Otherwise, use the standard logic
    if (inView > 0 && inView !== filteredTotal && inView !== total) {
      return "visibili";
    }
    if (hasFilters && filteredTotal !== total) {
      return "filtrati";
    }
    return "operatori";
  };

  const displayLabel = getDisplayLabel();

  // Format large numbers
  const formatNum = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return n.toLocaleString();
  };

  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={cn(
        "group flex items-center gap-3 pl-2 pr-3 py-1.5 rounded-full text-sm font-medium transition-all shadow-xl border",
        "bg-gradient-to-r from-card via-card to-card/95 backdrop-blur-sm",
        isOpen
          ? "border-[#284899]/60 shadow-[#284899]/20"
          : "border-border/60 hover:border-[#284899]/40 hover:shadow-[#284899]/10",
        disabled && "opacity-50 cursor-not-allowed"
      )}
      title="Visualizza statistiche dettagliate"
    >
      {/* Mini Donut Chart */}
      <div className="relative">
        <MiniDonut
          segments={categoryStats.map((c) => ({
            percentage: c.percentage,
            color: c.color,
          }))}
          size={36}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <BarChart3
            className={cn(
              "h-3.5 w-3.5 transition-colors",
              isOpen
                ? "text-[#284899]"
                : "text-muted-foreground group-hover:text-[#284899]"
            )}
          />
        </div>
      </div>

      {/* Stats Content */}
      <div className="flex items-center gap-3">
        {/* Visible Count */}
        <div className="flex flex-col items-start -space-y-0.5">
          <span
            className={cn(
              "text-base font-bold tabular-nums leading-tight",
              isOpen ? "text-[#284899]" : "text-foreground"
            )}
          >
            {formatNum(displayCount)}
          </span>
          <span className="text-[10px] text-muted-foreground leading-tight">
            {displayLabel}
          </span>
        </div>
      </div>

      {/* Expand Button with Text */}
      <div
        className={cn(
          "flex items-center gap-2 px-3 py-1.5 rounded-full transition-all",
          isOpen
            ? "bg-[#284899] text-white"
            : "bg-yellow-400 text-gray-900 font-semibold group-hover:bg-yellow-500 shadow-md"
        )}
      >
        <span className="text-sm font-semibold">
          Statistiche
        </span>
        <ChevronDown className={cn(
          "h-4 w-4 transition-transform",
          isOpen && "rotate-180"
        )} />
      </div>
    </button>
  );
}

export function MapSearch() {
  const { isLoading: authLoading } = useRequireAuth();
  const [showResults, setShowResults] = useState(true);
  // Track original categories selected in UI (before expansion)
  // Initialize with current filters to avoid sync issues
  const originalSelectedCategoriesRef = useRef<string[]>([]);


  const {
    results = [],
    setSelectedOperator,
    selectedOperator,
    filters,
    setFilters,
    clearFilters,
    drawnPolygon,
    setDrawnPolygon,
    isDrawingMode,
    setIsDrawingMode,
    isEditingMode,
    setIsEditingMode,
    drawingShapeType,
    savedSearches,
    loadedSearchId,
    loadedSearchSnapshot,
    setLoadedSearchSnapshot,
  } = useSearchStore();

  // Saved searches
  const {
    saveSearch,
    updateSearch,
    deleteSearch,
    loadSearch,
    revertToSnapshot,
    isSaving,
    isUpdating,
    isDeleting,
  } = useSavedSearches();
  const [showSavedMenu, setShowSavedMenu] = useState(false);
  const [searchName, setSearchName] = useState("");
  const savedMenuRef = useRef<HTMLDivElement>(null);

  // Shape menu state
  const [showShapeMenu, setShowShapeMenu] = useState(false);
  const shapeMenuRef = useRef<HTMLDivElement>(null);
  const editingSnapshotRef = useRef<GeoPolygon | null>(null);

  const startEditing = useCallback(() => {
    // Save a snapshot to allow cancel/revert
    editingSnapshotRef.current = drawnPolygon
      ? JSON.parse(JSON.stringify(drawnPolygon))
      : null;
    setIsEditingMode(true);
    setShowShapeMenu(false);
  }, [drawnPolygon, setIsEditingMode]);

  const confirmEditing = useCallback(() => {
    setIsEditingMode(false);
    editingSnapshotRef.current = null;
  }, [setIsEditingMode]);

  const cancelEditing = useCallback(() => {
    if (isEditingMode) {
      if (editingSnapshotRef.current) {
        setDrawnPolygon(editingSnapshotRef.current);
      }
      setIsEditingMode(false);
      editingSnapshotRef.current = null;
    }
    if (isDrawingMode) {
      setIsDrawingMode(false);
    }
  }, [isDrawingMode, isEditingMode, setDrawnPolygon, setIsDrawingMode, setIsEditingMode]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        shapeMenuRef.current &&
        !shapeMenuRef.current.contains(e.target as Node)
      ) {
        setShowShapeMenu(false);
      }
      if (
        savedMenuRef.current &&
        !savedMenuRef.current.contains(e.target as Node)
      ) {
        setShowSavedMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Check if current search can be saved
  const hasCategoryFilters = (filters.categories?.length || 0) > 0;
  const hasTypeFilters = (filters.types?.length || 0) > 0;

  const canSaveSearch =
    drawnPolygon ||
    hasCategoryFilters ||
    hasTypeFilters ||
    filters.searchText;

  // Handle save search
  const handleSaveSearch = async () => {
    if (!searchName.trim() || !canSaveSearch) return;

    try {
      await saveSearch({
        name: searchName.trim(),
        polygon: drawnPolygon,
        filters: {
          categories: filters.categories,
          types: filters.types,
          region: filters.region,
          searchText: filters.searchText,
          dateRange: filters.dateRange,
        },
      });
      setSearchName("");
      setShowSavedMenu(false);
    } catch (error) {
      console.error("Error saving search:", error);
    }
  };

  // Handle update existing search
  const handleUpdateSearch = async () => {
    if (!loadedSearchId) return;

    try {
      await updateSearch({
        searchId: loadedSearchId,
        polygon: drawnPolygon,
        filters: {
          categories: filters.categories,
          types: filters.types,
          region: filters.region,
          searchText: filters.searchText,
          dateRange: filters.dateRange,
        },
      });
      // Update snapshot to reflect the new saved state
      setLoadedSearchSnapshot({
        polygon: drawnPolygon,
        filters: {
          categories: filters.categories,
          types: filters.types,
          region: filters.region,
          searchText: filters.searchText,
          dateRange: filters.dateRange,
        },
      });
      setShowSavedMenu(false);
    } catch (error) {
      console.error("Error updating search:", error);
    }
  };

  // Get the currently loaded search name
  const loadedSearch = savedSearches.find((s) => s.id === loadedSearchId);

  // Check if current state differs from the loaded snapshot
  const hasChangesFromSnapshot = useMemo(() => {
    if (!loadedSearchId || !loadedSearchSnapshot) return false;

    // Compare polygon
    const polygonChanged =
      JSON.stringify(drawnPolygon) !==
      JSON.stringify(loadedSearchSnapshot.polygon);

    // Compare filters
    const snapshotFilters = loadedSearchSnapshot.filters;
    const filtersChanged =
      !areArraysEqual(filters.categories, snapshotFilters.categories as string[] | undefined) ||
      !areArraysEqual(filters.types, snapshotFilters.types as string[] | undefined) ||
      filters.searchText !== snapshotFilters.searchText;

    // Compare dateRange
    const currentDateRange = filters.dateRange;
    const snapshotDateRange = snapshotFilters.dateRange;
    const dateRangeChanged =
      currentDateRange?.start?.getTime() !==
      snapshotDateRange?.start?.getTime() ||
      currentDateRange?.end?.getTime() !== snapshotDateRange?.end?.getTime();

    return polygonChanged || filtersChanged || dateRangeChanged;
  }, [loadedSearchId, loadedSearchSnapshot, drawnPolygon, filters]);

  // Handle load saved search (toggle behavior - clicking active search deactivates it)
  const handleLoadSearch = (search: SavedSearch) => {
    if (search.id === loadedSearchId) {
      // Already active - deactivate it
      clearFilters();
      setSelectedRange(undefined);
    } else {
      // Load the search
      loadSearch(search);
    }
    setShowSavedMenu(false);
  };

  // Handle delete saved search
  const handleDeleteSearch = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await deleteSearch(id);
    } catch (error) {
      console.error("Error deleting search:", error);
    }
  };

  // Shape options config
  const shapeOptions: {
    type: DrawingShapeType;
    label: string;
    icon: typeof Pentagon;
  }[] = [
      { type: "polygon", label: "Poligono", icon: Pentagon },
      { type: "rectangle", label: "Rettangolo", icon: Square },
      { type: "circle", label: "Cerchio", icon: Circle },
    ];

  const { categoryTypes, categories } = useFilterOptions();

  // Ensure all categories from 'categories' are included in 'categoryTypes', even if they have no types
  // This fixes the issue where categories without operators or types are missing from the dropdown
  const completeCategoryTypes = useMemo(() => {
    if (!categories.length || !categoryTypes.length) return categoryTypes;

    const categoryTypesMap = new Map(categoryTypes.map(ct => [ct.category, ct]));
    const missingCategories: CategoryTypeGroup[] = [];

    for (const category of categories) {
      if (!categoryTypesMap.has(category)) {
        missingCategories.push({ category, types: [] });
      }
    }

    return [...categoryTypes, ...missingCategories];
  }, [categoryTypes, categories]);

  // Map category names to unified labels
  // Unify all hotel/accommodation variants to "Accomodation"
  // Keep other categories as original values from database
  const categoryLabelMap: Record<string, string> = {
    // Unify all hotel/accommodation variants to "Accomodation"
    "Hospitality": "Accomodation",
    "Hotel": "Accomodation",
    "Accommodation": "Accomodation",
    "Accomodation": "Accomodation",
    "Hotel & Alloggi": "Accomodation",
    "Hotel e Alloggi": "Accomodation",
    "Alloggi": "Accomodation",
    "Ospitalità": "Accomodation",
    // Keep DMO as original, not "DMO & Tourism"
    "DMO": "DMO",
    "DMO & Tourism": "DMO", // Map any "DMO & Tourism" to just "DMO"
    "Organizzazioni Turistiche": "Organizzazioni Turistiche", // Keep original
    // Keep other categories as original values
    "Bandiera Blu": "Bandiera Blu",
    "Locali Storici": "Locali Storici",
    "Sport": "Sport",
    "Fiere & Eventi": "Fiere & Eventi",
    "Attractions_Events": "Attractions_Events",
    "Non specificato": "Non specificato",
  };

  // Find all database categories that map to a given display category
  const getDatabaseCategoriesForDisplay = useCallback((displayCategory: string): string[] => {
    const dbCategories: string[] = [];
    for (const [dbCategory, mappedCategory] of Object.entries(categoryLabelMap)) {
      if (mappedCategory === displayCategory) {
        dbCategories.push(dbCategory);
      }
    }
    // Also include the display category itself if it exists in DB
    if (!dbCategories.includes(displayCategory)) {
      dbCategories.push(displayCategory);
    }
    return dbCategories;
  }, [categoryLabelMap]);

  // Convert expanded categories back to original categories for UI display
  // This ensures CategoryTypeSelect receives only categories that exist in categoryTypes
  // Memoized with stable dependencies to prevent unnecessary recalculations
  const getOriginalCategoriesFromExpanded = useCallback((expandedCategories: string[]): string[] => {
    if (!expandedCategories.length) return [];

    const original: string[] = [];
    const seen = new Set<string>();

    for (const expanded of expandedCategories) {
      // Check if this category exists in categoryTypes (can be selected in UI)
      const existsInCategoryTypes = categoryTypes.some(ct => ct.category === expanded);

      if (existsInCategoryTypes) {
        if (!seen.has(expanded)) {
          original.push(expanded);
          seen.add(expanded);
        }
      } else {
        // This category doesn't exist in categoryTypes, but might exist in completeCategoryTypes
        // Check if it exists in completeCategoryTypes first (for standalone categories like "Bandiera Blu")
        const existsInComplete = completeCategoryTypes.some(ct => ct.category === expanded);
        if (existsInComplete) {
          if (!seen.has(expanded)) {
            original.push(expanded);
            seen.add(expanded);
          }
        } else {
          // Category doesn't exist in categoryTypes or completeCategoryTypes, but might be a variant
          // Check if any category in categoryTypes maps to the same display category
          const mappedTo = categoryLabelMap[expanded];
          if (mappedTo) {
            // Find the first category in categoryTypes that maps to the same display category
            const matchingCategory = categoryTypes.find(ct => {
              const ctMappedTo = categoryLabelMap[ct.category];
              return ctMappedTo === mappedTo;
            });
            if (matchingCategory && !seen.has(matchingCategory.category)) {
              original.push(matchingCategory.category);
              seen.add(matchingCategory.category);
            }
          }
        }
      }
    }

    // Sort to ensure stable output
    return original.sort();
  }, [categoryTypes, categoryLabelMap, completeCategoryTypes]);

  // Reverse mapping: expand frontend category selections to database variants
  // When user selects "Accomodation" in UI, we need to query for all variants in DB
  const expandCategorySelection = useCallback((selectedCategories: string[]): string[] => {
    const expanded: string[] = [];
    const seen = new Set<string>();

    for (const selected of selectedCategories) {
      // Check if this selected category is a display category (mapped) or a database category
      const mappedTo = categoryLabelMap[selected];

      if (mappedTo) {
        // This is a database category that maps to a display category
        // Find all database categories that map to the same display category
        const displayCategory = mappedTo;
        const dbVariants = getDatabaseCategoriesForDisplay(displayCategory);
        for (const variant of dbVariants) {
          if (!seen.has(variant)) {
            expanded.push(variant);
            seen.add(variant);
          }
        }
      } else {
        // Check if this is a display category (like "Accomodation" that doesn't exist in DB)
        const dbVariants = getDatabaseCategoriesForDisplay(selected);
        if (dbVariants.length > 0 && dbVariants[0] !== selected) {
          // This is a display category, expand to all database variants
          for (const variant of dbVariants) {
            if (!seen.has(variant)) {
              expanded.push(variant);
              seen.add(variant);
            }
          }
        } else {
          // This is already a database category, use as-is
          if (!seen.has(selected)) {
            expanded.push(selected);
            seen.add(selected);
          }
        }
      }
    }

    return expanded;
  }, [categoryLabelMap, getDatabaseCategoriesForDisplay]);

  // Debug: log categories to verify they're loaded
  useEffect(() => {
    if (categories && categories.length > 0) {
      console.log("📊 Categories loaded from DB:", categories);
      const hospitalityCount = categories.filter((c: string) =>
        c.toLowerCase() === "hospitality"
      ).length;
      console.log(`🏨 Hospitality category found: ${hospitalityCount > 0 ? "YES" : "NO"}`);
    }
  }, [categories]);

  // Labels and colors are now imported from categoryConfig

  // Use Supercluster for fluid clustering
  const {
    clusters,
    allOperators,
    filteredOperators,
    operatorsInView,
    totalCount,
    isLoading,
    getClusterExpansionZoom,
    getClusterLeaves,
    unfilteredOperators,
    operatorsForCounting,
  } = useSupercluster();

  // Analytics panel state
  const [showAnalytics, setShowAnalytics] = useState(false);

  const handleOperatorClick = useCallback(
    (operator: OperatorWithCoords) => {
      setSelectedOperator(operator);
    },
    [setSelectedOperator]
  );

  const activeFiltersCount =
    (filters.categories?.length ? 1 : 0) +
    (filters.types?.length ? 1 : 0) +
    (filters.searchText ? 1 : 0) +
    (filters.dateRange?.start || filters.dateRange?.end ? 1 : 0) +
    (drawnPolygon ? 1 : 0);

  // Date filter state
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(
    filters.dateRange?.start || filters.dateRange?.end
      ? {
        from: filters.dateRange.start || undefined,
        to: filters.dateRange.end || undefined,
      }
      : undefined
  );
  const [showDatePicker, setShowDatePicker] = useState(false);
  const dateMenuRef = useRef<HTMLDivElement>(null);

  // Close date menu when clicking outside
  useEffect(() => {
    const handleClickOutsideDate = (e: MouseEvent) => {
      if (
        dateMenuRef.current &&
        !dateMenuRef.current.contains(e.target as Node)
      ) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideDate);
    return () =>
      document.removeEventListener("mousedown", handleClickOutsideDate);
  }, []);

  // Apply date filter when range changes
  const handleRangeSelect = (range: DateRange | undefined) => {
    setSelectedRange(range);
    if (range?.from || range?.to) {
      // When a period is set, automatically add "Fiere & Eventi" category if not already present
      const currentCategories = filters.categories || [];
      const fiereEventiCategory = "Fiere & Eventi";
      const hasFiereEventi = currentCategories.includes(fiereEventiCategory);
      
      setFilters({
        dateRange: {
          start: range.from || null,
          end: range.to || null,
        },
        // Add "Fiere & Eventi" category if not already present
        categories: hasFiereEventi 
          ? currentCategories 
          : [...currentCategories, fiereEventiCategory],
      });
    } else {
      setFilters({ dateRange: undefined });
    }
  };

  const handleDateClear = () => {
    setSelectedRange(undefined);
    setFilters({ dateRange: undefined });
  };

  const hasDateFilter = filters.dateRange?.start || filters.dateRange?.end;

  // Format date for display in button
  const formatDateShort = (date: Date | null | undefined) => {
    if (!date) return null;
    return date
      .toLocaleDateString("it-IT", { day: "numeric", month: "short" })
      .replace(".", "");
  };

  const dateRangeLabel = hasDateFilter
    ? [
      formatDateShort(filters.dateRange?.start),
      formatDateShort(filters.dateRange?.end),
    ]
      .filter(Boolean)
      .join(" → ") || "Period"
    : null;

  // Handle zone selection from autocomplete
  const handleZoneChange = (zone: Zone | null) => {
    if (zone) {
      // Zone selected from autocomplete: exact match only
      setFilters({
        searchText: zone.name,
        selectedZone: zone
      });
    } else {
      // Zone cleared
      setFilters({
        searchText: undefined,
        selectedZone: undefined
      });
    }
  };

  // Get current selected zone from filters
  const selectedZone = filters.selectedZone || null;

  const handleClearAll = () => {
    clearFilters();
    setSelectedRange(undefined);
  };

  // Handle ESC key to cancel drawing/editing mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        cancelEditing();
        if (showShapeMenu) setShowShapeMenu(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    cancelEditing,
    showShapeMenu,
  ]);

  if (authLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const resultsCount = results.length;

  // Common styles for filter buttons (solid opaque backgrounds)
  const filterButtonBase =
    "h-9 px-3 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 overlay-solid shadow-lg border border-border";
  const filterButtonDefault = "text-foreground hover:brightness-95";

  return (
    <div className="h-full relative overflow-hidden">
      {/* Full-width Map with Supercluster */}
      <MapView
        clusters={clusters}
        isLoading={isLoading}
        onOperatorClick={handleOperatorClick}
        getClusterExpansionZoom={getClusterExpansionZoom}
        getClusterLeaves={getClusterLeaves}
        filteredOperators={filteredOperators}
        className="absolute inset-0"
      />

      {/* Drawing/Editing mode visual indicator - subtle border glow */}
      <AnimatePresence>
        {(isDrawingMode || isEditingMode) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 pointer-events-none z-10 border-4 border-[#284899]/40 rounded-none"
            style={{
              boxShadow: "inset 0 0 40px rgba(245, 158, 11, 0.1)",
            }}
          />
        )}
      </AnimatePresence>

      {/* Inline Filter Bar - Top */}
      <div className="absolute top-3 left-3 right-16 z-[500]">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Zone Autocomplete */}
          <ZoneAutocomplete
            value={selectedZone?.name || filters.searchText || null}
            onChange={handleZoneChange}
            placeholder="Cerca comuni o regioni..."
            className="w-52"
            isLoadingOperators={isLoading}
            filteredCount={selectedZone ? filteredOperators.length : undefined}
            filteredOperators={allOperators}
          />

          {/* Draw Area - Dropdown with shapes or actions if area exists */}
          <div className="relative" ref={shapeMenuRef}>
            {/* Main button - special styling during drawing/editing */}
            {isDrawingMode || isEditingMode ? (
              <button
                onClick={() => {
                  setIsDrawingMode(false);
                  setIsEditingMode(false);
                }}
                className="h-9 px-3 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 bg-[#284899] text-white border border-[#1e3570] shadow-lg animate-pulse"
                title="Premi ESC per annullare"
              >
                {isDrawingMode ? (
                  drawingShapeType === "circle" ? (
                    <Circle className="h-3.5 w-3.5" />
                  ) : drawingShapeType === "rectangle" ? (
                    <Square className="h-3.5 w-3.5" />
                  ) : (
                    <Pentagon className="h-3.5 w-3.5" />
                  )
                ) : (
                  <Pencil className="h-3.5 w-3.5" />
                )}
                {isDrawingMode ? "Disegno..." : "Modifica..."}
              </button>
            ) : (
              <div
                onClick={() => setShowShapeMenu(!showShapeMenu)}
                className={cn(
                  "flex items-center h-9 rounded-lg text-sm font-medium transition-all overlay-solid shadow-lg border cursor-pointer",
                  "text-foreground hover:brightness-95",
                  drawnPolygon ? "border-[#284899]" : "border-border",
                  "pl-3"
                )}
                title={
                  drawnPolygon ? "Gestisci area" : "Disegna un'area sulla mappa"
                }
              >
                {/* Icon */}
                {drawnPolygon ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#284899] flex-shrink-0" />
                ) : (
                  <PenTool className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                )}

                {/* Label */}
                <span className="ml-1.5 pr-1">
                  {drawnPolygon ? "Area attiva" : "Disegna area"}
                </span>

                {/* Right section: X button and ChevronDown */}
                <div className="flex items-center gap-0.5 pr-1.5">
                  {drawnPolygon && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDrawnPolygon(null);
                      }}
                      className="p-0.5 text-muted-foreground hover:text-foreground transition-colors"
                      title="Rimuovi area"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                  <ChevronDown
                    className={cn(
                      "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
                      showShapeMenu && "rotate-180"
                    )}
                  />
                </div>
              </div>
            )}

            {/* Dropdown Menu */}
            <AnimatePresence>
              {showShapeMenu && !isDrawingMode && !isEditingMode && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full left-0 mt-1 z-[600] min-w-[160px]"
                >
                  <div className="overlay-solid rounded-lg shadow-xl border border-border overflow-hidden">
                    {drawnPolygon ? (
                      // Actions for existing area
                      <>
                        <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground border-b border-border">
                          Azioni area
                        </div>
                        <button
                          onClick={startEditing}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-secondary/50 transition-colors"
                        >
                          <Pencil className="h-4 w-4 text-[#284899]" />
                          Modifica
                        </button>
                        <button
                          onClick={() => {
                            setDrawnPolygon(null);
                            setShowShapeMenu(false);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-red-500/10 text-red-500 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                          Rimuovi
                        </button>
                        <div className="border-t border-border">
                          <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground">
                            Ridisegna come
                          </div>
                          {shapeOptions.map((shape) => (
                            <button
                              key={shape.type}
                              onClick={() => {
                                setDrawnPolygon(null);
                                setIsDrawingMode(true, shape.type);
                                setShowShapeMenu(false);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-secondary/50 transition-colors"
                            >
                              <shape.icon className="h-4 w-4 text-muted-foreground" />
                              {shape.label}
                            </button>
                          ))}
                        </div>
                      </>
                    ) : (
                      // Shape selection for new area
                      <>
                        <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground border-b border-border">
                          Scegli forma
                        </div>
                        {shapeOptions.map((shape) => (
                          <button
                            key={shape.type}
                            onClick={() => {
                              setIsDrawingMode(true, shape.type);
                              setShowShapeMenu(false);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-secondary/50 transition-colors"
                          >
                            <shape.icon className="h-4 w-4 text-[#284899]" />
                            {shape.label}
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Category + Type Combined Select */}
          <CategoryTypeSelect
            categoryTypes={completeCategoryTypes}
            selectedCategories={useMemo(() => {
              // Create stable key for memoization
              const categoriesKey = filters.categories ? [...filters.categories].sort().join(',') : '';

              // Check if we already have this result cached
              const cachedResult = originalSelectedCategoriesRef.current;
              const cachedKey = cachedResult.length > 0 ? [...cachedResult].sort().join(',') : '';

              // If categories haven't changed, return cached result
              if (categoriesKey && cachedKey === categoriesKey) {
                return cachedResult;
              }

              const result = getOriginalCategoriesFromExpanded(filters.categories || []);

              // Update ref only if result actually changed
              const resultKey = result.length > 0 ? [...result].sort().join(',') : '';
              if (resultKey !== cachedKey) {
                originalSelectedCategoriesRef.current = result;
              }

              return result;
            }, [filters.categories ? [...filters.categories].sort().join(',') : ''])}
            selectedTypes={filters.types || []}
            onCategoriesChange={(values) => {
              // Track previous original selections to detect deselections
              const previous = originalSelectedCategoriesRef.current;
              const current = values;

              // Early return ONLY if nothing changed AND ref is already set (not initial selection)
              // This prevents blocking legitimate selections
              const previousSorted = [...previous].sort().join(',');
              const currentSorted = [...current].sort().join(',');
              if (previous.length > 0 && previousSorted === currentSorted) {
                return;
              }

              // Find categories that were deselected
              const deselected = previous.filter(c => !current.includes(c));

              // Process current selections: if a category is selected, select all its variants
              const processed: string[] = [];
              const seen = new Set<string>();

              for (const category of current) {
                // Check if this category maps to a display category (like "Accomodation")
                const mappedTo = categoryLabelMap[category];
                if (mappedTo && mappedTo !== category) {
                  // This category is part of a group (e.g., "Hospitality" -> "Accomodation")
                  // Find all database categories in this group
                  const groupVariants = getDatabaseCategoriesForDisplay(mappedTo);
                  // Only include variants that exist in categoryTypes (are actually in the database)
                  const existingVariants = groupVariants.filter(v =>
                    categoryTypes.some(ct => ct.category === v)
                  );
                  for (const variant of existingVariants) {
                    if (!seen.has(variant)) {
                      processed.push(variant);
                      seen.add(variant);
                    }
                  }
                } else {
                  // This category is not part of a group, use as-is
                  if (!seen.has(category)) {
                    processed.push(category);
                    seen.add(category);
                  }
                }
              }

              // Handle deselections: if a category in a group is deselected, deselect all variants
              for (const deselectedCategory of deselected) {
                const mappedTo = categoryLabelMap[deselectedCategory];
                if (mappedTo && mappedTo !== deselectedCategory) {
                  // This deselected category is part of a group
                  // Remove all variants of this group from processed
                  const groupVariants = getDatabaseCategoriesForDisplay(mappedTo);
                  for (const variant of groupVariants) {
                    const index = processed.indexOf(variant);
                    if (index !== -1) {
                      processed.splice(index, 1);
                      seen.delete(variant);
                    }
                  }
                } else {
                  // Remove this category if it's in processed
                  const index = processed.indexOf(deselectedCategory);
                  if (index !== -1) {
                    processed.splice(index, 1);
                    seen.delete(deselectedCategory);
                  }
                }
              }

              // Update ref AFTER processing, but BEFORE setFilters to ensure consistency
              // Store original categories (from UI), not expanded ones
              originalSelectedCategoriesRef.current = current;

              // Expand to include all variants for backend query
              const expanded = expandCategorySelection(processed);

              // Update filters only if expanded categories actually changed
              // This prevents infinite loops while still allowing legitimate updates
              const expandedSorted = [...expanded].sort().join(',');
              const currentFiltersSorted = (filters.categories || []).sort().join(',');
              if (expandedSorted !== currentFiltersSorted) {
                setFilters({ categories: expanded.length ? expanded : undefined });
              } else {
                // Even if expanded is same, we need to ensure ref is updated for UI consistency
                // This handles cases where user selects a category that's already in the expanded list
                originalSelectedCategoriesRef.current = current;
              }
            }}
            onTypesChange={(values) =>
              setFilters({ types: values.length ? values : undefined })
            }
            placeholder="Categoria / Tipologia"
            className="w-56"
            labelMap={{
              ...typologyLabels,
              ...categoryLabelMap,
            }}
            colorMap={typologyColorMap}
            filteredOperators={filteredOperators}
            allOperators={operatorsForCounting.length > 0 ? operatorsForCounting : (unfilteredOperators.length > 0 ? unfilteredOperators : allOperators)}
          />

          {/* Date Range Filter */}
          <div className="relative" ref={dateMenuRef}>
            <div
              onClick={() => setShowDatePicker(!showDatePicker)}
              className={cn(
                "flex items-center h-9 rounded-lg text-sm font-medium transition-all overlay-solid shadow-lg border cursor-pointer",
                "text-foreground hover:brightness-95",
                hasDateFilter ? "border-[#284899]" : "border-border",
                "pl-3"
              )}
              title="Filter by period (events/fairs only)"
            >
              <CalendarDays className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              <span className="ml-1.5 pr-1">{dateRangeLabel || "Periodo"}</span>
              <div className="flex items-center gap-0.5 pr-1.5">
                {hasDateFilter && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDateClear();
                    }}
                    className="p-0.5 text-muted-foreground hover:text-foreground transition-colors"
                    title="Rimuovi filtro periodo"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
                    showDatePicker && "rotate-180"
                  )}
                />
              </div>
            </div>

            {/* Calendar Dropdown */}
            <AnimatePresence>
              {showDatePicker && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full left-0 mt-1 z-[600]"
                >
                  <div className="overlay-solid rounded-xl shadow-xl border border-border overflow-hidden p-3">
                    <p className="text-[10px] text-muted-foreground/70 mb-2 text-center">
                      Seleziona periodo evento/fiera
                    </p>
                    <DayPicker
                      mode="range"
                      selected={selectedRange}
                      onSelect={handleRangeSelect}
                      locale={it}
                      numberOfMonths={1}
                      showOutsideDays
                    />
                    {hasDateFilter && (
                      <div className="mt-2 pt-2 border-t border-border">
                        <button
                          onClick={handleDateClear}
                          className="w-full text-xs text-muted-foreground hover:text-red-500 py-1 transition-colors"
                        >
                          Rimuovi filtro
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Saved Searches Button */}
          <div className="relative" ref={savedMenuRef}>
            <button
              onClick={() => setShowSavedMenu(!showSavedMenu)}
              className={cn(
                filterButtonBase,
                showSavedMenu
                  ? "!bg-[#284899] !text-white !border-[#1e3570]"
                  : loadedSearchId
                    ? "border-[#284899] text-foreground"
                    : filterButtonDefault
              )}
              title="Ricerche salvate"
            >
              <Bookmark
                className={cn(
                  "h-3.5 w-3.5",
                  loadedSearchId &&
                  !showSavedMenu &&
                  "text-[#284899] fill-[#284899]"
                )}
              />
              {loadedSearchId && loadedSearch ? (
                <span className="truncate max-w-[100px]">
                  {loadedSearch.name}
                </span>
              ) : (
                "Ricerche salvate"
              )}
              {!loadedSearchId && savedSearches.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-[#284899] text-white rounded-full">
                  {savedSearches.length}
                </span>
              )}
              <ChevronDown
                className={cn(
                  "h-3 w-3 ml-0.5 transition-transform",
                  showSavedMenu && "rotate-180"
                )}
              />
            </button>

            {/* Saved Searches Dropdown */}
            <AnimatePresence>
              {showSavedMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full left-0 mt-1 z-[600] min-w-[280px]"
                >
                  <div className="overlay-solid rounded-xl shadow-xl border border-border overflow-hidden">
                    {/* Update Current Loaded Search */}
                    {loadedSearchId && loadedSearch && canSaveSearch && (
                      <div className="p-3 border-b border-border bg-[#284899]/5">
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-muted-foreground">
                                Filtro caricato:
                              </p>
                              <p className="text-sm font-medium text-foreground truncate">
                                {loadedSearch.name}
                              </p>
                            </div>
                            {!hasChangesFromSnapshot && (
                              <span className="text-xs text-emerald-500 flex items-center gap-1">
                                <CheckCircle2 className="h-3 w-3" />
                                Sincronizzato
                              </span>
                            )}
                          </div>
                          {hasChangesFromSnapshot && (
                            <div className="flex gap-2">
                              <Button
                                onClick={revertToSnapshot}
                                variant="outline"
                                size="sm"
                                className="flex-1 h-8 px-3"
                              >
                                <X className="h-3.5 w-3.5 mr-1.5" />
                                Annulla
                              </Button>
                              <Button
                                onClick={handleUpdateSearch}
                                disabled={isUpdating}
                                size="sm"
                                className="flex-1 h-8 px-3 bg-[#284899] hover:bg-[#1e3570]"
                              >
                                {isUpdating ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <>
                                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                                    Aggiorna
                                  </>
                                )}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Save Current Search */}
                    {canSaveSearch && (
                      <div className="p-3 border-b border-border">
                        <p className="text-xs text-muted-foreground mb-2">
                          {loadedSearchId
                            ? "Salva come nuova ricerca"
                            : "Salva ricerca attuale"}
                        </p>
                        <div className="flex gap-2">
                          <input
                            placeholder="Nome ricerca..."
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            onKeyDown={(e) =>
                              e.key === "Enter" && handleSaveSearch()
                            }
                            className="flex-1 h-8 rounded-lg border border-border bg-secondary px-2.5 text-xs text-foreground placeholder:text-muted-foreground focus:border-[#284899]"
                          />
                          <Button
                            onClick={handleSaveSearch}
                            disabled={!searchName.trim() || isSaving}
                            size="sm"
                            className="h-8 px-3"
                          >
                            {isSaving ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <BookmarkPlus className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Saved Searches List */}
                    <div className="max-h-64 overflow-y-auto">
                      {savedSearches.length > 0 ? (
                        <div className="py-1">
                          <div className="px-3 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                            Le tue ricerche
                          </div>
                          {savedSearches.map((search) => {
                            const isLoaded = search.id === loadedSearchId;
                            return (
                              <div
                                key={search.id}
                                className={cn(
                                  "group flex items-center gap-2 px-3 py-2 transition-colors cursor-pointer",
                                  isLoaded
                                    ? "bg-[#284899]/10 border-l-2 border-[#284899]"
                                    : "hover:bg-secondary/50"
                                )}
                                onClick={() => handleLoadSearch(search)}
                              >
                                <Bookmark
                                  className={cn(
                                    "h-3.5 w-3.5 flex-shrink-0",
                                    isLoaded
                                      ? "text-[#284899] fill-[#284899]"
                                      : "text-[#284899]"
                                  )}
                                />
                                <div className="flex-1 min-w-0">
                                  <p
                                    className={cn(
                                      "text-sm truncate transition-colors",
                                      isLoaded
                                        ? "text-[#284899] font-medium"
                                        : "text-foreground group-hover:text-[#284899]"
                                    )}
                                  >
                                    {search.name}
                                  </p>
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    {isLoaded && (
                                      <span className="text-[10px] px-1 py-0.5 rounded bg-[#284899]/20 text-[#1e3570]">
                                        Attiva
                                      </span>
                                    )}
                                    {search.polygon && (
                                      <span className="text-[10px] px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-600">
                                        Area
                                      </span>
                                    )}
                                    {search.filters &&
                                      Object.values(search.filters).some(
                                        Boolean
                                      ) && (
                                        <span className="text-[10px] px-1 py-0.5 rounded bg-blue-500/20 text-blue-600">
                                          Filtri
                                        </span>
                                      )}
                                  </div>
                                </div>
                                <button
                                  onClick={(e) =>
                                    handleDeleteSearch(search.id, e)
                                  }
                                  disabled={isDeleting}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-500/20 text-muted-foreground hover:text-red-500"
                                >
                                  {isDeleting ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-3 w-3" />
                                  )}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="px-3 py-6 text-center">
                          <Bookmark className="h-8 w-8 text-muted-foreground opacity-30 mx-auto mb-2" />
                          <p className="text-xs text-muted-foreground">
                            {canSaveSearch
                              ? "Dai un nome alla ricerca per salvarla"
                              : "Applica filtri o disegna un'area per salvare una ricerca"}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Clear All Filters */}
          {activeFiltersCount > 0 && (
            <button
              onClick={handleClearAll}
              className={cn(
                filterButtonBase,
                filterButtonDefault,
                "text-red-500 hover:!bg-red-500 hover:!text-white hover:!border-red-600"
              )}
            >
              <X className="h-3.5 w-3.5" />
              Cancella filtri
            </button>
          )}
        </div>
      </div>

      {/* Drawing/Editing Mode Instructions */}
      <AnimatePresence>
        {(isDrawingMode || isEditingMode) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-16 left-1/2 -translate-x-1/2 z-[500]"
          >
            <div className="flex items-center gap-3 px-4 py-2.5 rounded-full overlay-solid shadow-xl border-2 border-[#284899]/50 bg-[#284899]/10">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#284899] animate-pulse" />
                <span className="text-sm font-medium text-foreground">
                  {isEditingMode
                    ? "Trascina i vertici per modificare l'area"
                    : drawingShapeType === "circle"
                      ? "Clicca per il centro, poi clicca per il raggio"
                      : drawingShapeType === "rectangle"
                        ? "Clicca per un angolo, poi clicca per l'angolo opposto"
                        : "Clicca per aggiungere vertici"}
                </span>
              </div>
              {!isEditingMode && drawingShapeType === "polygon" && (
                <>
                  <div className="w-px h-4 bg-border" />
                  <span className="text-xs text-muted-foreground">
                    Doppio clic per completare
                  </span>
                </>
              )}
              <div className="w-px h-4 bg-border" />
              {isEditingMode ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={confirmEditing}
                    className="text-xs text-emerald-600 hover:text-emerald-500 transition-colors flex items-center gap-1 font-medium"
                  >
                    <CheckCircle2 className="h-3 w-3" />
                    Conferma
                  </button>
                  <div className="w-px h-4 bg-border" />
                  <button
                    onClick={cancelEditing}
                    className="text-xs text-muted-foreground hover:text-red-500 transition-colors flex items-center gap-1"
                  >
                    <X className="h-3 w-3" />
                    Annulla / ESC
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setIsDrawingMode(false);
                    setIsEditingMode(false);
                  }}
                  className="text-xs text-muted-foreground hover:text-red-500 transition-colors flex items-center gap-1"
                >
                  <X className="h-3 w-3" />
                  ESC
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Results Card - Right Side */}
      <AnimatePresence>
        {showResults && resultsCount > 0 && (
          <motion.div
            initial={{ opacity: 0, x: 10, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute top-3 right-3 z-[500] w-72 h-[70vh] flex flex-col"
          >
            <div className="overlay-solid rounded-xl shadow-2xl border border-border overflow-hidden flex flex-col flex-1 min-h-0">
              <ResultsList
                onOperatorSelect={handleOperatorClick}
                className="h-full"
                onClose={() => setShowResults(false)}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Show Results Button - Only when hidden */}
      <AnimatePresence>
        {!showResults && resultsCount > 0 && (
          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            onClick={() => setShowResults(true)}
            className="absolute top-3 right-3 z-[500] h-9 px-3 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 overlay-solid shadow-lg border border-border text-foreground hover:brightness-95"
            title="Mostra lista operatori"
          >
            <List className="h-4 w-4" />
            {resultsCount}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Status Bar - Bottom Center (Operatori Totali + Categorie + Stats Preview Chip) */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[500] flex items-center gap-3 px-4 max-w-[calc(100vw-2rem)] md:max-w-none md:px-0">
        {/* Operatori Totali - Compact version (hidden on mobile, also hidden when filters are active) */}
        {activeFiltersCount === 0 && (
          <div className="hidden md:flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full overlay-solid shadow-xl border border-border bg-gradient-to-r from-card via-card to-card/95 backdrop-blur-sm">
            {/* Map Pin Icon */}
            <svg
              className="w-4 h-4 text-[#284899] flex-shrink-0"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
            </svg>
            {/* Number and Label */}
            {isLoading ? (
              <div className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-[#284899] rounded-full animate-pulse" />
                <span className="text-xs text-muted-foreground/70">caricamento...</span>
              </div>
            ) : (
              <div className="flex flex-col items-start -space-y-0.5">
                <span className="text-base font-bold text-foreground leading-tight tabular-nums">
                  {totalCount.toLocaleString()}
                </span>
                <span className="text-[10px] text-muted-foreground leading-tight">
                  operatori totali
                </span>
              </div>
            )}
          </div>
        )}

        {/* Stats Preview Chip */}
        <StatsPreviewChip
          operators={allOperators}
          filteredOperators={filteredOperators}
          operatorsInView={operatorsInView}
          isLoading={isLoading}
          isOpen={showAnalytics}
          onToggle={() => setShowAnalytics(!showAnalytics)}
          selectedCategories={filters.categories || []}
          categoryLabelMap={categoryLabelMap}
        />
      </div>

      {/* Analytics Card */}
      <AnalyticsCard
        allOperators={allOperators}
        filteredOperators={filteredOperators}
        operatorsInView={operatorsInView}
        isOpen={showAnalytics}
        onClose={() => setShowAnalytics(false)}
      />

      {/* Shimmer animation style */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      {/* Selected Operator Card - Bottom Center, above status bar */}
      <AnimatePresence>
        {selectedOperator && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-20 left-1/2 -translate-x-1/2 z-[501] w-96 max-h-[60vh]"
          >
            <div className="overlay-solid rounded-xl shadow-lg border border-border p-4 max-h-[60vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-base text-foreground">
                      {selectedOperator.name}
                    </h3>
                    {selectedOperator.star_rating &&
                      selectedOperator.star_rating > 0 && (
                        <div className="flex items-center gap-0.5">
                          {Array.from({
                            length: Math.min(selectedOperator.star_rating, 5),
                          }).map((_, i) => (
                            <Star
                              key={i}
                              className="h-3 w-3 fill-[#284899] text-[#284899]"
                            />
                          ))}
                        </div>
                      )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {selectedOperator.macro_category && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#284899]/20 text-[#1e3570] font-medium">
                        {selectedOperator.macro_category}
                      </span>
                    )}
                    {selectedOperator.typology &&
                      (() => {
                        const config = getTypologyConfig(
                          selectedOperator.typology,
                          selectedOperator.macro_category
                        );
                        const TypologyIcon = config.icon;
                        return (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded font-medium flex items-center gap-1"
                            style={{
                              backgroundColor: `${config.color}20`,
                              color: config.color,
                            }}
                          >
                            <TypologyIcon className="h-3 w-3" />
                            {getTypologyLabel(selectedOperator.typology)}
                          </span>
                        );
                      })()}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 flex-shrink-0 text-muted-foreground hover:text-foreground"
                  onClick={() => setSelectedOperator(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Photo */}
              {selectedOperator.photo_url && (
                <div className="mt-3 rounded-lg overflow-hidden aspect-video bg-secondary">
                  <img
                    src={selectedOperator.photo_url}
                    alt={selectedOperator.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (
                        e.target as HTMLImageElement
                      ).parentElement!.style.display = "none";
                    }}
                  />
                </div>
              )}

              {/* Description */}
              {selectedOperator.description && (
                <p className="text-xs text-muted-foreground mt-3 line-clamp-3">
                  {selectedOperator.description}
                </p>
              )}

              {/* Details */}
              <div className="mt-3 pt-3 border-t border-border space-y-2">
                {/* Location */}
                {(selectedOperator.address ||
                  selectedOperator.city ||
                  selectedOperator.region) && (
                    <div className="flex items-start gap-2 text-xs">
                      <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-muted-foreground" />
                      <span className="text-foreground">
                        {[
                          selectedOperator.address,
                          selectedOperator.city,
                          selectedOperator.region,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </div>
                  )}

                {/* Phone */}
                {selectedOperator.phone && (
                  <div className="flex items-start gap-2 text-xs">
                    <Phone className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-muted-foreground" />
                    <div className="flex flex-col gap-1">
                      {selectedOperator.phone.split(/[,;]/).map((phone, i) => {
                        const trimmedPhone = phone.trim();
                        return (
                          <a
                            key={i}
                            href={`tel:${trimmedPhone.replace(/\s/g, "")}`}
                            className="text-foreground hover:text-[#284899] transition-colors"
                          >
                            {trimmedPhone}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Email */}
                {selectedOperator.email && (
                  <div className="flex items-start gap-2 text-xs">
                    <Mail className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-muted-foreground" />
                    <div className="flex flex-col gap-1 min-w-0">
                      {selectedOperator.email.split(/[,;]/).map((email, i) => {
                        const trimmedEmail = email.trim();
                        return (
                          <a
                            key={i}
                            href={`mailto:${trimmedEmail}`}
                            className="text-foreground hover:text-[#284899] transition-colors truncate"
                          >
                            {trimmedEmail}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Website */}
                {selectedOperator.website_url && (
                  <a
                    href={
                      selectedOperator.website_url.startsWith("http")
                        ? selectedOperator.website_url
                        : `https://${selectedOperator.website_url}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs text-foreground hover:text-[#284899] transition-colors"
                  >
                    <Globe className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                    <span className="truncate">
                      {selectedOperator.website_url.replace(/^https?:\/\//, "")}
                    </span>
                  </a>
                )}

                {/* Event dates */}
                {(selectedOperator.start_date || selectedOperator.end_date) && (
                  <div className="flex items-center gap-2 text-xs">
                    <Calendar className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                    <span className="text-foreground">
                      {selectedOperator.start_date &&
                        new Date(
                          selectedOperator.start_date
                        ).toLocaleDateString("it-IT")}
                      {selectedOperator.start_date &&
                        selectedOperator.end_date &&
                        " → "}
                      {selectedOperator.end_date &&
                        new Date(selectedOperator.end_date).toLocaleDateString(
                          "it-IT"
                        )}
                    </span>
                  </div>
                )}

                {/* Certifier */}
                {selectedOperator.certifying_body && (
                  <div className="flex items-center gap-2 text-xs">
                    <Award className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                    <span className="text-foreground">
                      {selectedOperator.certifying_body}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
